import React from 'react';
import styled from 'styled-components';
import Button from '@material-ui/core/Button';
import Icon from '@mdi/react';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import {mdiCloudCog, mdiServerNetwork, mdiWan, mdiCashRegister} from '@mdi/js';
import Tabs from '../../interfaces/Tabs';
import POSState from '../../interfaces/POSState';

//#region styles
const StyledTabContainer = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;
  margin-top: 18px;
  padding: 8px 8px 4px 8px;
  background: ${props => props.theme.disabledStateBlue}66;
`;

const StyledTab = styled(Button)`
  display: flex;
  font-size: 20px;
  padding: 0px 0px 0px 0px;
  color: ${props => props.theme.grey};
  text-decoration: none;
  font-family: 'Trade Gothic Bold Condensed';
  font-weight: 800;
  text-transform: uppercase;
  border-bottom: 6px solid transparent;
  border-radius: 0;
  transition: 250ms;
  .MuiButton-label {
    margin-bottom: 5px;
    gap: 5px;
  }
  &.MuiButton-root {
    line-height: 17px;
  }
  &:hover {
    text-decoration: none;
    color: ${props => props.theme.white};
    border-bottom: 6px solid ${props => props.theme.white};
  }
  &.active {
    color: ${props => props.theme.white};
    border-bottom: 6px solid ${props => props.theme.white};
  }
`;

const StyledIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  height: 24px;
  width: 24px;
`;

const StyledAlertsNotification = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
`;

//#endregion styles

//#region Constants
export const tabsDictionary: Record<string, Tabs> = {
  CLOUD_SERVICES: Tabs.CLOUD_SERVICES,
  SWITCH_CONNECTIVITY: Tabs.SWITCH_CONNECTIVITY,
  NETWORK_CIRCUIT: Tabs.NETWORK_CIRCUIT,
  REGISTERS: Tabs.REGISTERS,
};

//#endRegion Constants
interface HeaderTabsProps {
  selectedTab: string;
  handleTabChange: (tab: Tabs) => void;
  state: POSState;
}

function HeaderTabs({state, selectedTab, handleTabChange}: HeaderTabsProps) {
  const {
    cloudServices,
    networkCircuits,
    ipads,
    ipadsMini,
    receiptPrinters,
    barcodeScanners,
    cashDrawers,
    paymentTerminals,
  } = state;

  const handleClick = (tab: Tabs) => {
    handleTabChange(tab);
  };

  return (
    <StyledTabContainer>
      <StyledTab
        className={'cloud-services' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.CLOUD_SERVICES)}
      >
        <StyledIconContainer>
          <Icon path={mdiCloudCog} size={1} />
          <StyledAlertsNotification>
            <SeverityBadge severity={getPriorityAlert(cloudServices)} size={0.75} />
          </StyledAlertsNotification>
        </StyledIconContainer>
        Cloud Services
      </StyledTab>
      <StyledTab
        className={'network-circuit' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.NETWORK_CIRCUIT)}
      >
        <StyledIconContainer>
          <Icon path={mdiWan} size={0.875} />
          <StyledAlertsNotification>
            <SeverityBadge severity={getPriorityAlert(networkCircuits)} size={0.75} />
          </StyledAlertsNotification>
        </StyledIconContainer>
        Network Circuit
      </StyledTab>
      <StyledTab
        className={'switch-connectivity' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.SWITCH_CONNECTIVITY)}
      >
        <Icon path={mdiServerNetwork} size={1} /> Switch Connectivity
      </StyledTab>

      <StyledTab
        className={'registers' === selectedTab ? 'active' : ''}
        onClick={() => handleClick(Tabs.REGISTERS)}
      >
        <StyledIconContainer>
          <Icon path={mdiCashRegister} size={0.75} />
          <StyledAlertsNotification>
            <SeverityBadge
              severity={getPriorityAlert([
                ...ipads,
                ...ipadsMini,
                ...receiptPrinters,
                ...barcodeScanners,
                ...cashDrawers,
                ...paymentTerminals,
              ])}
              size={0.75}
            />
          </StyledAlertsNotification>
        </StyledIconContainer>
        Registers
      </StyledTab>
    </StyledTabContainer>
  );
}

export default HeaderTabs;
